import React, { useRef, useState, useEffect, useContext } from "react"
import { TrackballControls } from "./custom-controls"

import { useFrame, extend, useThree } from "react-three-fiber"
import { AppContext } from '../../context'
import {useCurrentSize} from '../../utils/useCurrentSize';
import * as THREE from "three"

import lerp from "lerp";
import { isMobile } from "react-device-detect"

extend({ TrackballControls });

function fitCameraToSelection(camera, controls, selection, fitOffset = 1.2, setPan) {
    
    const box = new THREE.Box3();

    for (const object of selection) box.expandByObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    const maxSize = Math.max(size.x, size.y, size.z);
    const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
    const fitWidthDistance = fitHeightDistance / camera.aspect;
    const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);
    
    const direction = controls.target.clone()
        .sub(camera.position)
        .normalize()
        .multiplyScalar(distance);

    controls.maxDistance = distance;
    controls.target.copy(center);

    setPan && setPan({
        center: {x:center.x, y:center.y},
        max: {x:size.x, y:size.y}
    })

    camera.near = distance / 100;
    camera.far = distance * 100;
    camera.updateProjectionMatrix();

    camera.position.copy(controls.target).sub(direction);

    controls.update();
}

export const Controls = () => {
    const { camera, gl } = useThree();
    const controls = useRef();
    const context = useContext(AppContext);
    const { maxPanX, sizeRatioDefault, disableControlsForSinglePage, selection, zoomDelta, setZoomDelta, setAllowZoomIn, setAllowZoomOut, pageStep, suspendedControls, zoom} = context;

    const pageFactor = selection ? 1 : pageStep/2;
    const defaultPan = {
        center: {x:0, y:0},
        max: {x:maxPanX* pageFactor, y:3}
    };
    const [pan, setPan] = useState(defaultPan);
    const ratio = selection ? selection.userData.ratioFull : sizeRatioDefault;
    const maxSize = 10 * ratio ;
    const center = new THREE.Vector3(0, 0, 0);
    const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
    const minFitRatio =  pageStep === 1 ? 0.9 : 1.1;
    const maxFitRatio =  pageStep === 1 ? 0.9 : 0.9;
    
    const calculateDistance = () => {
        const fitRatio = camera.aspect < 1 ? minFitRatio : maxFitRatio;
        return fitHeightDistance / Math.min(camera.aspect,1) * fitRatio;
    }

    useFrame(() => {
        const distance = calculateDistance();
        const theControls = controls.current;
        updateControls(context, theControls, camera, distance, setAllowZoomIn, setAllowZoomOut, pan);       
    })
    

    const {width, height} = useCurrentSize();
    useEffect(() => {
        const distance = calculateDistance();
        const theControls = controls.current;
        const direction = theControls.target.clone()
            .sub(camera.position)
            .normalize()
            .multiplyScalar(distance);

        theControls.target.copy(center);
        camera.near = distance / 100;
        camera.far = distance * 100;
        camera.updateProjectionMatrix();
        camera.position.copy(theControls.target).sub(direction);

        theControls.handleResize();
        updateControls(context, theControls, camera, distance, setAllowZoomIn, setAllowZoomOut, pan);       
    },[ pageStep, width, height, fitHeightDistance])

    useEffect(() => {
        if (selection) {
            fitCameraToSelection(camera, controls.current, [selection], 3, setPan);            
        } else {
            setPan(defaultPan);
        }
    }, [selection, ratio]);
    useEffect(() => {
        if (zoomDelta !== null) {
            controls.current.requestZoom(zoomDelta);
            setZoomDelta(null)
        }        
    }, [zoomDelta]);
    
    const disableControls = suspendedControls || (disableControlsForSinglePage && pageStep === 1 && !selection);
    return (
        <trackballControls
            args={[camera, gl.domElement]}
            ref={controls}
            noRotate
            noZoom={disableControls}
            noPan={disableControls}
            maxDistance={zoom.max}
            minDistance={zoom.min}
            dynamicDampingFactor={0.1}
            zoomSpeed={0.8}
            panSpeed={isMobile ? 0.1 : 0.6}
        />
    )
}
const EPS = 0.00001;
const v = new THREE.Vector3();  
const minPan = new THREE.Vector3();
const maxPan = new THREE.Vector3();

function updateControls(context, theControls, camera, distance, setAllowZoomIn, setAllowZoomOut, pan) {
    theControls.maxDistance = distance;
    theControls.update()
    context.zoom.current = theControls.target.distanceTo(camera.position);
    context.zoom.max = distance;
    setAllowZoomIn(context.zoom.current > context.zoom.min + EPS);
    setAllowZoomOut(context.zoom.current < context.zoom.max - EPS);
    context.offset = theControls.getOffset();
    const t = (distance - context.zoom.current) / (distance - context.zoom.min);
    const x = lerp(0, pan.max.x, t);
    const y = lerp(0, pan.max.y, t);
    maxPan.x = pan.center.x + x;
    maxPan.y = pan.center.y + y;
    minPan.x = pan.center.x - x;
    minPan.y = pan.center.y - y;    

    v.copy(theControls.target);
    theControls.target.clamp(minPan, maxPan);
    v.sub(theControls.target);
    camera.position.sub(v);
}
